import { Component, Emit, Prop, PropSync, Vue } from 'nuxt-property-decorator';
import confirmOrderDoc from '../../confirm-order.gql';

/**
 * Field structure for address information display
 */
interface AddressField {
  label: string;
  value: string;
}

/**
 * Order information interface for confirm order modal
 */
export interface OrderInformation {
  id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  province: string;
  city: string;
  district: string;
  postcode: string;
}

/**
 * Confirm Order Modal Component
 *
 * @export
 * @class SubscriptionConfirmOrderModalComponent
 * @extends {Vue}
 */
@Component({})
export default class SubscriptionConfirmOrderModalComponent extends Vue {
  @PropSync('isOpen', { type: Boolean, default: false }) modalIsOpen!: boolean;

  @Prop({
    type: Object,
    default: () => ({
      id: '',
      name: '',
      phone: '',
      email: '',
      address: '',
      province: '',
      city: '',
      district: '',
      postcode: ''
    })
  })
  readonly order!: OrderInformation;

  protected readonly loading = {
    confirmOrder: false
  };

  /**
   * Two-dimensional array containing address fields organized in rows
   * Each row contains two fields (label-value pairs)
   * This is computed from the order prop to be reactive
   */
  get addressFields(): AddressField[][] {
    return [
      // Row 1: Name, Phone Number
      [
        { label: 'Name', value: this.order.name || '-' },
        { label: 'Phone Number', value: this.order.phone || '-' }
      ],
      // Row 2: Email, Address
      [
        { label: 'Email', value: this.order.email || '-' },
        { label: 'Address', value: this.order.address || '-' }
      ],
      // Row 3: Province, City
      [
        { label: 'Province', value: this.order.province || '-' },
        { label: 'City', value: this.order.city || '-' }
      ],
      // Row 4: District, Postcode
      [
        { label: 'District', value: this.order.district || '-' },
        { label: 'Postcode', value: this.order.postcode || '-' }
      ]
    ];
  }

  @Emit('confirmed')
  protected emitConfirmed(): {
    orderId: string;
  } {
    return {
      orderId: this.order.id
    };
  }

  async confirmOrder(): Promise<void> {
    this.loading.confirmOrder = true;
    try {
      const mutation = confirmOrderDoc;
      await this.$api.mutate({
        mutation,
        variables: {
          id: this.order?.id
        }
      });
      this.emitConfirmed();
      this.$emitter.emit('TOAST', 'Confirmation Order Successfully');
    } catch (error) {
      this.$emitter.emit('TOAST', error);
    } finally {
      this.loading.confirmOrder = false;
    }
  }
}
